import React, { useEffect, useRef, useState } from "react";

/**
 * Reusable voice-search (speech-to-text) mic button.
 * Usage: <VoiceSearchButton onResult={(text) => setSearch(text)} />
 * lang: "en-US" (default) or "ne-NP" for Nepali. Needs HTTPS or localhost + mic permission.
 */
export default function VoiceSearchButton({ onResult, lang = "en-US", title = "Search by voice" }) {
    const [listening, setListening] = useState(false);
    const [supported, setSupported] = useState(true);
    const recRef = useRef(null);
    const cbRef = useRef(onResult);

    useEffect(() => { cbRef.current = onResult; }, [onResult]);

    useEffect(() => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { setSupported(false); return; }

        const rec = new SR();
        rec.lang = lang;
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.continuous = false;

        rec.onresult = (e) => {
            const transcript = Array.from(e.results).map((r) => r[0].transcript).join(" ").trim();
            if (transcript && cbRef.current) cbRef.current(transcript);
        };
        rec.onend = () => setListening(false);
        rec.onerror = () => setListening(false);

        recRef.current = rec;
        return () => { try { rec.abort(); } catch (_) { } };
    }, [lang]);

    const toggle = (e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        const rec = recRef.current;
        if (!rec) return;
        if (listening) { try { rec.stop(); } catch (_) { } setListening(false); }
        else { try { rec.start(); setListening(true); } catch (_) { } }
    };

    if (!supported) return null;

    return (
        <button type="button" onClick={toggle} title={title} aria-label={title}
            className={`dn-voice-btn ${listening ? "listening" : ""}`}>
            <i className={`bi ${listening ? "bi-mic-fill" : "bi-mic"}`}></i>
            <style>{`
                .dn-voice-btn { border: 0; background: transparent; cursor: pointer; color: #2563eb; padding: 0 6px; display: inline-flex; align-items: center; }
                .dn-voice-btn i { font-size: 1.15rem; }
                .dn-voice-btn.listening { color: #e23744; }
                .dn-voice-btn.listening i { animation: dn-mic-pulse 1s infinite; }
                @keyframes dn-mic-pulse { 0% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.18); opacity: 0.55; } 100% { transform: scale(1); opacity: 1; } }
            `}</style>
        </button>
    );
}
